import { useState, useRef, useEffect } from "react";
import { Download, FileText, Music, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";

export default function ExportMenu({ songId, songTitle, transpose = 0, hasChords, hasLyrics }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const url = kind === "chords"
        ? `/songs/${songId}/export/chords?transpose=${transpose}`
        : `/songs/${songId}/export/lyrics`;
      const res = await api.get(url, { responseType: "blob" });
      const slug = (songTitle || "song").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_|_$/g, "") || "song";
      const fname = kind === "chords" ? `${slug}_chord_chart.txt` : `${slug}_lyric_sheet.txt`;
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success(`${kind === "chords" ? "Chord chart" : "Lyric sheet"} downloaded`);
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="export-menu-btn"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full bg-[#131822] px-3 py-2 text-sm font-medium text-slate-300 ring-1 ring-[#222A38] transition-colors hover:text-white"
      >
        <Download size={16} /> <span className="hidden sm:inline">Export</span>
      </button>
      {open && (
        <div
          data-testid="export-menu"
          className="absolute right-0 top-full z-50 mt-2 w-60 overflow-hidden rounded-xl border border-[#222A38] bg-[#12161A]/98 p-1.5 shadow-2xl backdrop-blur-md"
        >
          <div className="px-2.5 py-1.5 font-mono-sw text-[10px] uppercase tracking-wider text-slate-500">
            Export (DEMO data)
          </div>
          <button
            data-testid="export-chords"
            disabled={!hasChords || busy}
            onClick={() => download("chords")}
            className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left text-sm text-slate-200 transition-colors hover:bg-white/5 disabled:opacity-40"
          >
            {busy === "chords" ? <Loader2 size={16} className="animate-spin text-cyan-400" /> : <Music size={16} className="text-cyan-400" />}
            <span className="flex-1">
              Chord chart
              {transpose !== 0 && <span className="ml-1 font-mono-sw text-[11px] text-cyan-400">({transpose > 0 ? `+${transpose}` : transpose})</span>}
              {!hasChords && <span className="block text-[11px] text-slate-500">Not analyzed</span>}
            </span>
          </button>
          <button
            data-testid="export-lyrics"
            disabled={!hasLyrics || busy}
            onClick={() => download("lyrics")}
            className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left text-sm text-slate-200 transition-colors hover:bg-white/5 disabled:opacity-40"
          >
            {busy === "lyrics" ? <Loader2 size={16} className="animate-spin text-amber-400" /> : <FileText size={16} className="text-amber-400" />}
            <span className="flex-1">
              Lyric sheet
              {!hasLyrics && <span className="block text-[11px] text-slate-500">Not analyzed</span>}
            </span>
          </button>
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] text-slate-500">
            <Check size={11} className="text-emerald-400" /> Downloads as a printable .txt file
          </div>
        </div>
      )}
    </div>
  );
}
